import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const sections = [
  { id: 'section-1', number: '1', title: '事業者情報' },
  { id: 'section-2', number: '2', title: '取得する個人情報' },
  { id: 'section-3', number: '3', title: '個人情報の利用目的' },
  { id: 'section-4', number: '4', title: '個人情報の取得方法' },
  { id: 'section-5', number: '5', title: '個人データの第三者提供' },
  { id: 'section-6', number: '6', title: '委託先の管理' },
  { id: 'section-7', number: '7', title: '安全管理措置' },
  { id: 'section-8', number: '8', title: 'Cookie等の利用について' },
  { id: 'section-9', number: '9', title: '保有個人データの開示・訂正・利用停止等' },
  { id: 'section-10', number: '10', title: '漏えい等への対応' },
  { id: 'section-11', number: '11', title: '個人情報保護方針の改定' },
  { id: 'section-12', number: '12', title: 'お問い合わせ窓口' },
];

const PrivacyPolicyPage = () => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sinax.co.jp';

    document.title = 'Privacy Policy / 個人情報保護方針 | 株式会社SINAX';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setProperty = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', '株式会社SINAXの個人情報保護方針（Privacy Policy）です。当社が取得する個人情報の利用目的、管理方法、第三者提供に関する方針をご確認いただけます。');
    setMeta('keywords', 'SINAX,個人情報保護方針,プライバシーポリシー,Privacy Policy');
    setProperty('og:title', 'Privacy Policy / 個人情報保護方針 | 株式会社SINAX');
    setProperty('og:description', '株式会社SINAXの個人情報保護方針です。');
    setProperty('og:url', `${siteUrl}/privacy-policy`);
    setProperty('og:type', 'website');

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy / 個人情報保護方針',
      description: '株式会社SINAXの個人情報保護方針',
      url: `${siteUrl}/privacy-policy`,
      publisher: {
        '@type': 'Organization',
        name: '株式会社SINAX',
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'privacy-policy-schema';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('privacy-policy-schema');
      if (s) document.head.removeChild(s);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-white border-b border-gray-100">
          <div className="px-8 lg:px-16 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="font-sans text-xs text-warmgray tracking-[0.3em] mb-4 uppercase">
                Legal
              </p>
              <h1 className="font-serif text-4xl lg:text-5xl text-darkgray font-bold mb-6 leading-tight">
                Privacy Policy
                <span className="block text-2xl lg:text-3xl font-medium mt-2 text-gray-500">
                  個人情報保護方針
                </span>
              </h1>
              <p className="font-sans text-sm text-gray-500 leading-relaxed max-w-2xl">
                株式会社SINAX（以下「当社」といいます。）は、当社が取得する個人情報の重要性を認識し、
                その適正な取得・利用・管理を行うことが社会的責務であると考えております。
                当社は、個人情報の保護に関する法令その他の規範を遵守し、個人情報を適切に取り扱います。
              </p>
              <p className="font-sans text-xs text-warmgray mt-6">
                制定日：<time dateTime="2026-03-23">2026年3月23日</time>
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content Area */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="px-8 lg:px-16 max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">

              {/* Table of Contents */}
              <motion.aside
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="lg:w-64 flex-shrink-0"
              >
                <div className="lg:sticky lg:top-28 bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h2 className="font-sans text-xs text-warmgray tracking-[0.2em] uppercase mb-5">
                    目次
                  </h2>
                  <nav>
                    <ol className="space-y-2.5">
                      {sections.map((s) => (
                        <li key={s.id}>
                          <button
                            onClick={() => scrollToSection(s.id)}
                            className="text-left w-full font-sans text-sm text-gray-600 hover:text-darkgray transition-colors duration-200 leading-snug cursor-pointer"
                          >
                            <span className="text-warmgray mr-1.5">{s.number}.</span>
                            {s.title}
                          </button>
                        </li>
                      ))}
                    </ol>
                  </nav>
                </div>
              </motion.aside>

              {/* Main Body */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="flex-1 min-w-0"
              >
                <div className="space-y-14">

                  {/* Section 1 */}
                  <article id="section-1">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">1.</span>
                      事業者情報
                    </h3>
                    <dl className="space-y-3">
                      {[
                        { label: '事業者名', value: '株式会社SINAX' },
                        {
                          label: '住所',
                          value: '〒101-0041\n東京都千代田区神田須田町1丁目7番8号　VORT秋葉原Ⅳ 2F',
                        },
                        { label: '代表者名', value: '村永 裕一' },
                      ].map((item) => (
                        <div key={item.label} className="flex flex-col sm:flex-row sm:gap-6">
                          <dt className="font-sans text-sm text-warmgray w-28 flex-shrink-0 mb-0.5 sm:mb-0 pt-0.5">
                            {item.label}
                          </dt>
                          <dd className="font-sans text-sm text-darkgray leading-relaxed whitespace-pre-line">
                            {item.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </article>

                  {/* Section 2 */}
                  <article id="section-2">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">2.</span>
                      取得する個人情報
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose mb-4">
                      当社は、以下のような個人情報を取得することがあります。
                    </p>
                    <ul className="space-y-2.5">
                      {[
                        'お問い合わせフォーム等を通じて取得する氏名、会社名、部署名、役職、メールアドレス、電話番号、お問い合わせ内容',
                        'お取引先ご担当者様に関する連絡先情報、契約・商談に関する情報',
                        '採用応募者に関する氏名、連絡先、職務経歴書その他採用選考に必要な情報',
                        '当社サービス、コンサルティング、SES、受託開発、アプリ開発等の提供に伴い取得する情報',
                        '当社ウェブサイトの利用に伴い取得されるCookie、IPアドレス、閲覧履歴等の情報',
                      ].map((item, i) => (
                        <li key={i} className="flex gap-3 font-sans text-sm text-gray-600 leading-relaxed">
                          <span className="text-warmgray mt-0.5 flex-shrink-0">・</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </article>

                  {/* Section 3 */}
                  <article id="section-3">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">3.</span>
                      個人情報の利用目的
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose mb-4">
                      当社は、取得した個人情報を、以下の目的の範囲内で利用します。
                    </p>
                    <ul className="space-y-2.5">
                      {[
                        '当社へのお問い合わせへの対応、ご連絡、ご相談への回答のため',
                        '当社サービスに関するご案内、商談、契約締結、履行、運用、保守およびアフターフォローのため',
                        'SES事業、コンサルティング事業、受託開発事業、アプリ開発事業における業務連絡、要員提案、案件対応、サービス提供のため',
                        'お取引先との連絡、請求、支払、契約管理その他取引遂行のため',
                        '採用応募者への連絡、採用選考、入社手続のため',
                        '当社ウェブサイトの改善、品質向上、利用状況の分析のため',
                        '法令等に基づく対応のため',
                      ].map((item, i) => (
                        <li key={i} className="flex gap-3 font-sans text-sm text-gray-600 leading-relaxed">
                          <span className="text-warmgray mt-0.5 flex-shrink-0">・</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </article>

                  {/* Section 4 */}
                  <article id="section-4">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">4.</span>
                      個人情報の取得方法
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose">
                      当社は、適法かつ公正な手段により個人情報を取得します。<br />
                      また、取得にあたっては、必要に応じて利用目的を明示または公表します。
                    </p>
                  </article>

                  {/* Section 5 */}
                  <article id="section-5">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">5.</span>
                      個人データの第三者提供
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose mb-4">
                      当社は、法令に基づく場合を除き、あらかじめご本人の同意を得ることなく、個人データを第三者に提供いたしません。<br />
                      ただし、以下の場合はこの限りではありません。
                    </p>
                    <ul className="space-y-2.5">
                      {[
                        '法令に基づく場合',
                        '人の生命、身体または財産の保護のために必要がある場合',
                        '公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合',
                        '国の機関または地方公共団体等に協力する必要がある場合',
                        '利用目的の達成に必要な範囲で、個人データの取扱いを委託する場合',
                      ].map((item, i) => (
                        <li key={i} className="flex gap-3 font-sans text-sm text-gray-600 leading-relaxed">
                          <span className="text-warmgray mt-0.5 flex-shrink-0">・</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </article>

                  {/* Section 6 */}
                  <article id="section-6">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">6.</span>
                      委託先の管理
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose">
                      当社は、業務遂行上必要な範囲で個人データの取扱いを外部に委託する場合があります。<br />
                      この場合、当社は委託先に対し、適切な安全管理措置を講じるよう必要かつ適切な監督を行います。
                    </p>
                  </article>

                  {/* Section 7 */}
                  <article id="section-7">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">7.</span>
                      安全管理措置
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose mb-4">
                      当社は、個人データの漏えい、滅失、毀損等を防止し、その他個人データを適切に管理するため、
                      必要かつ適切な安全管理措置を講じます。主な内容は以下のとおりです。
                    </p>
                    <ul className="space-y-2.5">
                      {[
                        '個人情報の取扱責任者の設置',
                        '個人情報の取扱ルールの整備',
                        '従業者に対する教育および秘密保持の徹底',
                        'アクセス権限の管理、不正アクセス対策等の技術的措置',
                        '書類、端末、記録媒体等の盗難・紛失防止等の物理的措置',
                        '委託先に対する適切な監督',
                      ].map((item, i) => (
                        <li key={i} className="flex gap-3 font-sans text-sm text-gray-600 leading-relaxed">
                          <span className="text-warmgray mt-0.5 flex-shrink-0">・</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </article>

                  {/* Section 8 */}
                  <article id="section-8">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">8.</span>
                      Cookie等の利用について
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose">
                      当社ウェブサイトでは、利便性向上、利用状況の把握、サイト改善等のためにCookieその他これに類する技術を使用する場合があります。
                      これらの情報には、単独では特定の個人を識別できない情報が含まれます。<br /><br />
                      なお、お使いのブラウザ設定によりCookieの受け取りを拒否することも可能ですが、
                      その場合、当社ウェブサイトの一部機能がご利用いただけないことがあります。
                    </p>
                  </article>

                  {/* Section 9 */}
                  <article id="section-9">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">9.</span>
                      保有個人データの開示・訂正・利用停止等
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose">
                      当社は、ご本人またはその代理人から、当社が保有する個人データについて、
                      利用目的の通知、開示、訂正、追加、削除、利用停止、消去、第三者提供の停止等のお申し出を受けた場合には、
                      ご本人確認を行ったうえで、法令に従い適切に対応します。<br /><br />
                      具体的なお手続については、下記お問い合わせ窓口までご連絡ください。<br />
                      なお、法令の定めにより、ご要望にお応えできない場合があります。
                    </p>
                  </article>

                  {/* Section 10 */}
                  <article id="section-10">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">10.</span>
                      漏えい等への対応
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose">
                      当社は、個人データの漏えい等が発生した場合には、法令に従い、
                      必要に応じて監督機関への報告およびご本人への通知その他適切な対応を行います。
                    </p>
                  </article>

                  {/* Section 11 */}
                  <article id="section-11">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5 pb-3 border-b border-gray-200">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">11.</span>
                      個人情報保護方針の改定
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-loose">
                      当社は、法令の改正、運用状況の見直しその他必要に応じて、本方針を改定することがあります。<br />
                      改定後の内容は、当社ウェブサイト上に掲載した時点から適用されるものとします。
                    </p>
                  </article>

                  {/* Section 12 */}
                  <article id="section-12" className="bg-gray-50 rounded-xl p-8 border border-gray-100">
                    <h3 className="font-serif text-xl font-bold text-darkgray mb-5">
                      <span className="text-warmgray font-sans text-base font-normal mr-2">12.</span>
                      お問い合わせ窓口
                    </h3>
                    <dl className="space-y-3 mb-6">
                      {[
                        { label: '会社名', value: '株式会社SINAX' },
                        { label: '窓口名称', value: '個人情報お問い合わせ窓口' },
                        {
                          label: '住所',
                          value: '〒101-0041\n東京都千代田区神田須田町1丁目7番8号　VORT秋葉原Ⅳ 2F',
                        },
                        { label: 'メール', value: 'info@sinax.co.jp' },
                      ].map((item) => (
                        <div key={item.label} className="flex flex-col sm:flex-row sm:gap-6">
                          <dt className="font-sans text-xs text-warmgray w-24 flex-shrink-0 mb-0.5 sm:mb-0 pt-0.5 tracking-wide">
                            {item.label}
                          </dt>
                          <dd className="font-sans text-sm text-darkgray leading-relaxed whitespace-pre-line">
                            {item.label === 'メール' ? (
                              <a
                                href={`mailto:${item.value}`}
                                className="text-darkgray hover:text-warmgray transition-colors duration-200 underline underline-offset-2"
                              >
                                {item.value}
                              </a>
                            ) : (
                              item.value
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <div className="pt-5 border-t border-gray-200">
                      <Link
                        to="/contact"
                        className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-2.5 bg-darkgray text-white text-sm font-sans tracking-wide rounded-md cursor-pointer transition-all duration-300 hover:bg-opacity-80"
                      >
                        お問い合わせフォームへ
                        <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center" />
                      </Link>
                    </div>
                  </article>

                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default PrivacyPolicyPage;
